'use client';

import { useEffect, useState } from 'react';
import { UploadedImage } from "@/components/shared/UploadedImage";
import { SectionLabel } from "@/components/shared/SectionLabel";
import { useTranslations } from "@/i18n/intl";
import { getFootprints } from "@/lib/actions/footprints";
import { Link } from "@/i18n/routing";
import { RichText } from "@/components/shared/RichText";

export function FootprintsSection() {
  const t = useTranslations("Footprints");
  const tCommon = useTranslations("Common");
  const [footprints, setFootprints] = useState<any[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getFootprints().then((data) => {
      if (cancelled) return;
      setFootprints(data);
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready || footprints.length === 0) return null;

  return (
    <section className="py-10 md:py-[72px] px-5 md:px-[40px] bg-white">
      <div className="max-w-[1170px] mx-auto">
        <div className="text-left mb-12">
          <SectionLabel text={tCommon("firmOverview")} />
          <h2 className="text-[32px] md:text-[40px] font-semibold text-charcoal leading-[1.08] mb-4 md:mr-[220px]">
            {t("title")}
          </h2>
          <p className="text-[16px] text-gray font-sans leading-[1.56]">
            {t("description")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {footprints.map((item) => (
            <Link key={item.id} href={`/insights?industry=${item.slug}`}>
              <div
                key={item.id}
                className="group relative aspect-[4/5] rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500"
              >
                {/* Background Image */}
                {item.image ? (
                  <UploadedImage
                    src={item.image}
                    alt={item.title}
                    fill
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                ) : (
                  <div className="w-full h-full bg-navy/10 flex items-center justify-center text-gray-400">
                    -
                  </div>
                )}

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" />

                {/* Content Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 z-10 flex flex-col justify-end min-h-[50%]">
                  <h3 className="text-[20px] md:text-[22px] font-bold text-white font-heading leading-tight mb-3 transform group-hover:-translate-y-1 transition-transform duration-500">
                    {item.title}
                  </h3>
                  <RichText
                    html={item.description || ''}
                    className="text-[13px] text-white/80 leading-relaxed line-clamp-3 opacity-100 group-hover:-translate-y-1 transition-all duration-500 delay-100 [&_p]:mb-0 [&_p]:text-white/80"
                  />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
